import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, Paper, Typography } from '@mui/material';
import darkBarcode from '../../assets/images/darkbarcode.png';
import scannerIcon from '../../assets/images/Bar_code_scan.png';
import { useMessageContextManager } from '../../components/MessageOverlay/MessageOverlay';
import {
	DARK_MODE,
	OPERATIONS,
	PART_VALIDATION_ERROR,
	PART_VALIDATION_LOADING,
	REDIRECT_URLS,
	WORK_STREAM_TYPE,
} from '../../constants';
import { useRedirectToNextScreen } from '../../hooks';
import { ScanOrEnterBarcodeForm } from '../../components';
import {
	receivePart,
	updateReceivePart,
} from '../../redux/actionCreators/receivePart';
import { receiveMrPart } from '../../redux/actionCreators/majorRepair';
import { updateCurrentLineItem } from '../../redux/actionCreators/station';
import { updateOperations } from '../../redux/actionCreators/operations';
import ModeToggleSwitch from '../../components/ToggleSwitch/ToggleSwitch';
import { useEffect, useState } from 'react';
import { updateModeType } from '../../redux/actionCreators/modeType';
import { store } from '../../redux/store';
import { getNextOperation } from '../../utils/operationFlow';
import styles from './ReceivePart.module.scss';

const classNames = {
	paper: {
		height: '100%',
		display: 'flex',
	},
	textInputContainer: {
		width: { sm: '100%', md: '80%' },
	},
};

const mapStateToProps = (state) => {
	return {
		allowedOperations: state.operations.allowedOperations,
		themeMode: state.theme.themeMode,
		modeType: state.modeType,
	};
};

const mapDispatchToProps = (dispatch) => ({
	receivePart: (serialNumber, successCallback, errorCallback) =>
		dispatch(receivePart(serialNumber, successCallback, errorCallback)),
	receiveMrPart: (serialNumber, successCallback, errorCallback) =>
		dispatch(receiveMrPart(serialNumber, successCallback, errorCallback)),
	updateReceivePart: (data) => dispatch(updateReceivePart(data)),
	updateCurrentLineItem: (data) => dispatch(updateCurrentLineItem(data)),
	updateModeType: (value) => dispatch(updateModeType(value)),
});

export const ReceivePart = ({
	receivePart,
	receiveMrPart,
	updateReceivePart,
	updateCurrentLineItem,
	themeMode,
	updateModeType,
	modeType,
	allowedOperations,
}) => {
	const { showMessageOverlay } = useMessageContextManager();
	const [changeModeType, setChangeModeType] = useState(false);
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const redirect = useRedirectToNextScreen();

	useEffect(() => {
		if (allowedOperations?.includes(OPERATIONS.TRANSFER_DROP)) {
			dispatch(updateOperations({ currentOperation: OPERATIONS.TRANSFER_DROP }));
			navigate('/transfer-drop', { replace: true });
			return;
		}
		dispatch(updateOperations({ currentOperation: OPERATIONS.RECEIVE_PART }));
	}, [allowedOperations, dispatch, navigate]);

const navigateAfterReceiveSuccess = () => {
    const state = store.getState();
    const completed = state.station.currentLineItem?.completedOperations || [];
    const allowedOps = state.operations.allowedOperations || [];

    const hasCompletedPartValidation = completed.includes(OPERATIONS.PART_VALIDATION);
    const hasPendingMachineOperation =
        allowedOps.includes(OPERATIONS.MACHINE) &&
        !completed.includes(OPERATIONS.MACHINE);
    const isSinglePartValidationFlow =
        allowedOps.filter(
            (op) => op !== OPERATIONS.TAKE_IN_OUT && op !== OPERATIONS.RECEIVE_PART,
        ).length === 1 &&
        allowedOps.includes(OPERATIONS.PART_VALIDATION);

    if (isSinglePartValidationFlow && hasCompletedPartValidation) {
        dispatch(updateOperations({ currentOperation: OPERATIONS.PART_VALIDATION }));
        navigate(REDIRECT_URLS[OPERATIONS.PART_VALIDATION]);
        return;
    }

    if (hasCompletedPartValidation && hasPendingMachineOperation) {
        dispatch(updateOperations({ currentOperation: OPERATIONS.PART_VALIDATION }));
        navigate(REDIRECT_URLS[OPERATIONS.PART_VALIDATION]);
        return;
    }

    const navigable = allowedOps.filter(
        op => op !== OPERATIONS.TAKE_IN_OUT && op !== OPERATIONS.RECEIVE_PART
    );

    // Saare navigable ops complete hain → next VIN ke liye pehla op navigate karo
    const allDone = navigable.every(op => completed.includes(op));
    if (allDone) {
        const firstOp = navigable[0]; // pehla actual operation
        if (firstOp) {
            dispatch(updateOperations({ currentOperation: firstOp }));
            navigate(REDIRECT_URLS[firstOp]);
        }
        return;
    }

    const nextOp = getNextOperation(completed, allowedOps);
    dispatch(updateOperations({ currentOperation: nextOp }));
    const workStreamType = state.user.workStreamType;

    if (nextOp === OPERATIONS.DROP || nextOp === OPERATIONS.RECEIVE_SN) {
        if (workStreamType === WORK_STREAM_TYPE.POOL) navigate('/scan-vin');
        else navigate('/');
    } else {
        navigate(REDIRECT_URLS[nextOp] || '/scan-vin');
    }
};

	const scanSerialNumber = (serialNumber, resetBarcodeValue) => {
		const updatedScannedValue = serialNumber.substring(0, 17);

		const closeMesssageOverlay = showMessageOverlay({
			contextText: PART_VALIDATION_LOADING.SERIAL_NUMBER_ERROR_CONTEXT_TEXT,
			loading: true,
		});

		const isMrStation = allowedOperations?.includes(OPERATIONS.MAJOR_REPAIR);
		const receiveFn = isMrStation ? receiveMrPart : receivePart;

		receiveFn(
			updatedScannedValue,
			() => {
				closeMesssageOverlay();

				if (isMrStation) {
					dispatch(updateOperations({ currentOperation: OPERATIONS.MAJOR_REPAIR }));
					navigate(REDIRECT_URLS[OPERATIONS.MAJOR_REPAIR]);
				} else if (allowedOperations.includes(OPERATIONS.BOOK)) {
					redirect(() => {}, { redirectUrl: '/assembly/book' });

				} else if (allowedOperations.includes(OPERATIONS.WCPA)) {
					dispatch(updateOperations({ currentOperation: OPERATIONS.WCPA }));
					navigate(REDIRECT_URLS[OPERATIONS.WCPA]);

				} else if (allowedOperations.includes(OPERATIONS.PROCESS_VALIDATION)) {
					redirect(() => {}, { redirectUrl: '/assembly/process-validation' });

				} else if (
					allowedOperations.includes(OPERATIONS.MACHINE) &&
					!allowedOperations.includes(OPERATIONS.PART_VALIDATION) &&
					!allowedOperations.includes(OPERATIONS.PROCESS_VALIDATION) &&
					!allowedOperations.includes(OPERATIONS.BOOK)
				) {
					dispatch(updateOperations({ currentOperation: OPERATIONS.MACHINE }));
					navigate(REDIRECT_URLS[OPERATIONS.MACHINE]);

				} else {
					navigateAfterReceiveSuccess();
				}
			},
			(error) => {
				resetBarcodeValue();
				showMessageOverlay({
					contextText:
						PART_VALIDATION_ERROR.ERROR_CONTEXT_TEXT + 'Scanning Serial Number',
					info: error?.response?.data?.message || 'Error occurred while receiving vehicle',
					error: true,
				});
			},
		);
	};

	useEffect(() => {
		if (modeType === 'Manual Mode') {
			setChangeModeType(true);
		}
	}, [modeType]);

	useEffect(() => {
		const getValue = changeModeType ? 'Manual Mode' : 'Auto Mode';
		updateModeType(getValue);
	}, [changeModeType]);

	const handleSwitch = () => {
		setChangeModeType((prev) => !prev);
	};

	return (
		<Box
			className={`${styles.outerContainer} ${
				themeMode === DARK_MODE
					? styles.outerContainerDark
					: styles.outerContainerLight
			}`}
		>
			<Paper
				className={`${styles.tableContentContainer} ${
					themeMode === DARK_MODE
						? styles.tableContentContainerDark
						: styles.tableContentContainerLight
				}`}
			>
				<Grid
					container
					direction="column"
					margin="auto"
					columnSpacing={2}
					sx={{
						flexGrow: 1,
						width: '60%',
						verticalAlign: 'center',
						justifyContent: 'center',
					}}
				>
					<Grid
						container
						className={styles.centeredContainer}
						paddingX={1}
						paddingTop={5}
						paddingBottom={2}
					>
						<Grid
							item
							display="flex"
							alignItems="center"
							sx={{ marginRight: '16px' }}
						>
							<Box className={styles.scannerIconBox}>
								<img
									src={darkBarcode}
									alt="scan"
									className={styles.scannerImage}
								/>
							</Box>
						</Grid>

						<Grid item xs={10} md={10} lg={10}>
							<ScanOrEnterBarcodeForm
								enableAutoEnter
								isAlphaNumericValidation={false}
								onSubmit={scanSerialNumber}
								nameOfComponent="ReceivePart"
							/>
						</Grid>
					</Grid>
				</Grid>
			</Paper>
		</Box>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(ReceivePart);